import {Product} from 'components/customer/Product/entity/Product';
import {useJobCabinetFieldUpdate} from 'components/customer/Product/helpers/useJobCabinetFieldUpdate';
import {useJobContext} from 'contexts/JobContext';
import {useNotificationContext} from 'contexts/NotificationContext';
import {cloneDeep} from 'lodash';
import {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {getProduct} from 'service/ProductService';
import {genericMessageHandler} from 'shared/helpers';
import {PartialJob} from 'shared/types/PartialJob';
import {useAppDispatch} from 'store/customer';
import {removeChange, Type} from 'store/customer/appSlice';
import {NumberSchema, ObjectSchema, number, object} from 'yup';
import {invalidateParticularRoomAndCost} from 'components/customer/Product/store/productApi';

export interface FieldData {
    name: string;
    label: string;
    hidden?: boolean;
    autoUpdateValue?: {
        field: string;
        value: string;
    };
}

const CORNER_45 = 2;

const FIELD_MAP: {[key: string]: FieldData} = {
    cabinet_height: {
        name: 'height',
        label: 'Height',
    },
    cabinet_width: {
        name: 'width',
        label: 'Width',
    },
    cabinet_depth: {
        name: 'depth',
        label: 'Depth',
    },
    cabinet_right_width: {
        name: 'width',
        label: 'Right Width',
    },
    cabinet_left_width: {
        name: 'width2',
        label: 'Left Width',
    },
    cabinet_right_depth: {
        name: 'length_depth',
        label: 'Right Depth',
    },
    cabinet_left_depth: {
        name: 'width_depth',
        label: 'Left Depth',
    },
    cabinet_panel_width: {
        name: 'width',
        label: 'Width',
    },
    cabinet_panel_height: {
        name: 'height',
        label: 'Height',
    },
    cabinet_applied_panel_depth: {
        name: 'applied_panel_depth',
        label: 'Depth',
    },
    cabinet_upper_filler_depth: {
        name: 'upper_filler_depth',
        label: 'Depth',
    },
    cabinet_width_door_1: {
        name: 'width',
        label: 'Width 1',
    },
    cabinet_width_door_2: {
        name: 'width2',
        label: 'Width 2',
    },
};

export interface Dimensions {
    height?: number;
    width?: number;
    depth?: number;

    width2?: number;
    length_depth?: number;
    width_depth?: number;

    cabinet_panel_width?: number;
    cabinet_panel_height?: number;

    upper_filler_depth?: number;
    applied_panel_depth?: number;
}

type ErrorWithMessage = {
    message: string;
};

type FieldType = 'width' | 'height' | 'depth';

export const useProductDimension = (
    productDetails: Product,
    roomId?: number
) => {
    const {notify} = useNotificationContext();
    const dispatch = useAppDispatch();
    const [dimensions, setDimensions] = useState<Dimensions>({});
    const [fields, setFields] = useState<FieldData[]>([]);
    const [showForm, setShowForm] = useState(false);
    const [validator, setValidator] =
        useState<ObjectSchema<null, Dimensions>>();

    const {job, room: contextRoom} = useJobContext() as PartialJob;
    const room = {id: roomId ?? contextRoom?.id};

    const navigate = useNavigate();

    const {isLoading, updateFields} = useJobCabinetFieldUpdate(room.id);

    const toggleForm = useCallback((event?: React.MouseEvent) => {
        if (event) {
            event.stopPropagation();
        }
        setShowForm((showForm) => !showForm);
    }, []);

    const resetRedirectionCheck = useCallback(() => {
        dispatch(
            removeChange({
                id: Number(productDetails.job_cabinet_id),
                type: Type.PRODUCT_DIMENSION,
            })
        );
    }, []);

    const getMinMaxValidation = useCallback(
        async (
            schema: NumberSchema,
            productType: number,
            fieldType: FieldType
        ) => {
            const product = await getProduct(productType);

            if (product) {
                switch (fieldType) {
                    case 'width':
                        if (product.maxWidth) {
                            schema = schema.max(
                                Number(product.maxWidth),
                                `Width cannot be more than ${product.maxWidth} mm`
                            );
                        }

                        if (product.minWidth) {
                            schema = schema.min(
                                Number(product.minWidth),
                                `Width cannot be less than ${product.minWidth} mm`
                            );
                        }
                        break;
                    case 'depth':
                        if (product.maxDepth) {
                            schema = schema.max(
                                Number(product.maxDepth),
                                `Depth cannot be more than ${product.maxDepth} mm`
                            );
                        }

                        if (product.minDepth) {
                            schema = schema.min(
                                Number(product.minDepth),
                                `Depth cannot be less than ${product.minDepth} mm`
                            );
                        }
                        break;
                    case 'height':
                        if (product.maxHeight) {
                            schema = schema.max(
                                Number(product.maxHeight),
                                `Height cannot be more than ${product.maxHeight} mm`
                            );
                        }

                        if (product.minHeight) {
                            schema = schema.min(
                                Number(product.minHeight),
                                `Height cannot be less than ${product.minHeight} mm`
                            );
                        }
                        break;
                }
            }

            return schema;
        },
        []
    );

    const initForm = useCallback(
        async (fields: FieldData[], productDetails: Product) => {
            if (fields && fields.length > 0) {
                let formSchema = object<Dimensions, null>();
                const initialValues: Dimensions = {};

                for (const field of fields) {
                    // key is used as form field names which needs to be sent to backend
                    // productKey is used to get the value from productDetails (data fetched from backend)
                    // Error suppression is used because we are sure that the key exists in the Dimensions interface
                    // and they are not in any manner touched by the user.

                    // But, if any one has any better idea to handle this, please feel free to suggest.
                    const key = field.name as keyof Dimensions;
                    const productKey = field.name as keyof Product;

                    if (productKey in productDetails) {
                        // eslint-disable-next-line security/detect-object-injection -- please check the above comment
                        const value = productDetails[productKey];
                        if (value) {
                            // eslint-disable-next-line security/detect-object-injection -- please check the above comment
                            initialValues[key] = Number(value) || 0;

                            if (!field.hidden) {
                                let fieldType: FieldType = 'width';

                                if (field.name.includes('height'))
                                    fieldType = 'height';
                                else if (field.name.includes('depth'))
                                    fieldType = 'depth';

                                const schema = await getMinMaxValidation(
                                    number(),
                                    productDetails.type,
                                    fieldType
                                );

                                formSchema = formSchema.shape({
                                    [field.name]: schema.required(),
                                });
                            }
                        }
                    }
                }

                setDimensions(initialValues);
                setValidator(formSchema);
            }
        },
        []
    );

    useEffect(() => {
        if (!showForm) {
            resetRedirectionCheck();
        }
    }, [showForm]);

    useEffect(() => {
        if (productDetails.hasOwnProperty('dimensions')) {
            const dimensionFields = cloneDeep(productDetails.dimension_fields);

            const dimensionString = productDetails.dimensions;
            // Regular expression to match the pattern `height x (width1 x width2)`
            const regex = /(\d+)\s*x\s*\((\d+)\s*&\s*(\d+)\)/;
            const matches = dimensionString.match(regex);
            let hideWidth = false;

            if (matches) {
                hideWidth = true;
                dimensionFields.push(
                    'cabinet_width_door_1',
                    'cabinet_width_door_2'
                );
            }

            const fields: FieldData[] = [];
            dimensionFields.forEach((field) => {
                if (field in FIELD_MAP) {
                    const data = cloneDeep(FIELD_MAP[String(field)]);
                    let hidden = false;

                    if (field === 'cabinet_width' && hideWidth) {
                        hidden = true;
                    }

                    if (
                        field === 'cabinet_panel_width' &&
                        dimensionFields.includes('cabinet_width')
                    ) {
                        hidden = true;
                    }

                    if (
                        field === 'cabinet_panel_height' &&
                        dimensionFields.includes('cabinet_height')
                    ) {
                        hidden = true;
                    }

                    if (productDetails.cabinet_l_shape_type == CORNER_45) {
                        if (field == 'cabinet_left_width') {
                            data.autoUpdateValue = {
                                field: 'length_depth',
                                value: 'width2 - width + width_depth',
                            };
                        }

                        if (field == 'cabinet_right_width') {
                            data.autoUpdateValue = {
                                field: 'width_depth',
                                value: 'width - width2 + length_depth',
                            };
                        }

                        if (field == 'cabinet_left_depth') {
                            data.autoUpdateValue = {
                                field: 'length_depth',
                                value: 'width2 - width + width_depth',
                            };
                        }

                        if (field == 'cabinet_right_depth') {
                            data.autoUpdateValue = {
                                field: 'width_depth',
                                value: 'width - width2 + length_depth',
                            };
                        }
                    }

                    if (
                        !fields.map((field) => field.name).includes(data.name)
                    ) {
                        fields.push({
                            ...data,
                            hidden,
                        } as FieldData);
                    }
                }
            });

            setFields(fields);
        }
    }, [productDetails]);

    useEffect(() => {
        void initForm(fields, productDetails);
    }, [fields, productDetails]);

    const formHandler = useCallback(
        async (values: Dimensions) => {
            toggleForm();

            try {
                await updateFields({
                    ...values,
                    job_cabinet_id: productDetails.id,
                });

                dispatch(invalidateParticularRoomAndCost(room.id));

                genericMessageHandler(
                    notify,
                    {
                        message: 'Product updated successfully',
                    },
                    'success'
                );
            } catch (e) {
                const params = new URLSearchParams({
                    product: productDetails.type.toString(),
                    cabinetId: productDetails.id.toString(),
                });

                if (e instanceof Error) {
                    const errorMessage = (e as ErrorWithMessage).message;
                    genericMessageHandler(
                        notify,
                        errorMessage
                            .split(';')
                            .map((message: string) => message)
                    );

                    if (e.name != 'Error') {
                        params.append('error', e.name);
                    }
                }

                for (const [key, value] of Object.entries(values) as [
                    string,
                    number,
                ][]) {
                    if (value === undefined) continue;

                    if (key.includes('width')) {
                        params.append('width', value.toString());
                    }
                    if (key.includes('depth')) {
                        params.append('depth', value.toString());
                    }
                    if (key.includes('height')) {
                        params.append('height', value.toString());
                    }
                }

                void navigate(
                    `/v2/job/${job.displayId}/room/${
                        productDetails.room_id
                    }/product?${params.toString()}`
                );
            }
        },
        [toggleForm, productDetails, job]
    );

    return {
        dimensions,
        fields,
        showForm,
        validator,
        toggleForm,
        formHandler,
        savingProduct: isLoading,
    };
};
